import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { UpcomingActivity as UpcomingActivityIcon } from "@dreamhub/designsystem/icons";
import {
  useDeleteActivityMutation,
  useEntityActivitiesFetch
} from "@/entities/activities/hooks";
import ActivityItem from "@/components/layouts/Activities/components/ActivityItem";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import { useSplitActivityByTimes } from "@/entities/activities/timehook";
import {
  Activity,
  ActivityData,
  EntityActivityRequest,
  NewActivity
} from "@/entities/activities/types";
import CounterBadge from "@/components/ui/counter-badge";
import { createModalStateSetter } from "@/entities/modals/helpers";
import IconContainer from "@/components/ui/icon-container";
import { forEach, map } from "lodash";
import OptionsDropdownMenu, {
  DetailedViewOptions
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import {
  activityTypeToIconMap,
  activityTypeToNewActivityType
} from "@/components/layouts/Activities/components/Activity";
import { newActivityToActivityType } from "@/components/layouts/Activities/Activities";
import { getEnumTKey } from "@/utils/strings";
import {
  ActivityModalState,
  useActivityModals
} from "@/components/layouts/Activities/hooks/useActivityModals";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import { DealData } from "@/pages/deals/types";
import { LeadData } from "@/pages/leads/types";
import { PersonData } from "@/entities/people/types";
import { CompanyData } from "@/pages/companies/types";
import { PartnerData } from "@/pages/partners/types";
import { ContractData } from "@/pages/contracts/types";

const UPCOMING_ACTIVITIES_REQUEST: EntityActivityRequest = {
  flags: { includeCancelledMeetings: false, includeRecurrentInvites: true }
};

interface Props {
  entity:
    | DealData
    | LeadData
    | PersonData
    | CompanyData
    | PartnerData
    | ContractData
    | undefined;
  entityType:
    | EntityType.DEAL
    | EntityType.LEAD
    | EntityType.PERSON
    | EntityType.COMPANY
    | EntityType.PARTNER
    | EntityType.CONTRACT;
  isQuickView?: boolean;
}

const UpcomingActivities: React.FC<Props> = ({
  entity,
  entityType,
  isQuickView = false
}) => {
  const { t } = useTranslation();
  const pageName = entityTypeToEntityName[entityType];

  const { isDropdownClosing } = useDropdownClosing();

  const entityActivitiesFetch = useEntityActivitiesFetch(pageName);
  const deleteActivity = useDeleteActivityMutation(pageName);
  const [activities, setActivities] = useState<ActivityData[]>([]);
  const [deleteActivityId, setDeleteActivityId] = useState<string | null>(null);

  useEffect(() => {
    if (!entity?.id) return;
    entityActivitiesFetch.mutate(
      { pathParams: { entityId: entity.id }, data: UPCOMING_ACTIVITIES_REQUEST },
      {
        onSuccess: (response) => setActivities(response.activities),
        onError: () => setActivities([])
      }
    );
  }, [entity?.id, pageName]);

  const [activityModalState, setActivityModalState] =
    useState<ActivityModalState>({
      newActivityType: null,
      isOpen: false,
      isEditMode: false
    });

  const setIsActivityModalOpen = createModalStateSetter(setActivityModalState);

  const { upcomingActivities } = useSplitActivityByTimes(activities, [
    Activity.CALL,
    Activity.IN_PERSON_MEETING,
    Activity.ONLINE_MEETING
  ]);

  const refetchActivities = useCallback(() => {
    if (!entity?.id) return;
    entityActivitiesFetch.mutate(
      { pathParams: { entityId: entity.id }, data: UPCOMING_ACTIVITIES_REQUEST },
      {
        onSuccess: (response) => setActivities(response.activities),
        onError: () => setActivities([])
      }
    );
  }, [entity?.id, pageName]);

  const { modals: activityModals } = useActivityModals({
    activityModalState,
    isDropdownClosing,
    entityData: entity,
    setIsActivityModalOpen,
    resetActivitiesInfinite: refetchActivities,
    entityType,
    pageName
  });

  const handleOpenEdit = (activity: ActivityData) => {
    const newActivityType =
      activityTypeToNewActivityType[activity.type as Activity];
    setActivityModalState({
      isOpen: true,
      newActivityType,
      isEditMode: true,
      editActivityData: activity
    });
  };

  const handleDeleteActivity = () => {
    if (!deleteActivityId || !entity?.id) return;
    deleteActivity.mutate(
      { pathParams: { entityId: entity.id, activityId: deleteActivityId } },
      {
        onSuccess: () => {
          setDeleteActivityId(null);
          refetchActivities();
        },
        onError: () => setDeleteActivityId(null)
      }
    );
  };

  const upcomingActivityTypes = [NewActivity.CALL, NewActivity.MEETING];

  const getActivityTypeOptions = () => {
    const options = {} as DetailedViewOptions;
    forEach(upcomingActivityTypes, (newActivity) => {
      options[newActivity] = {
        action: () => {
          setActivityModalState({
            isOpen: true,
            newActivityType: newActivity,
            isEditMode: false
          });
        },
        customIcon: (
          <IconContainer size="5" color="secondary">
            {
              activityTypeToIconMap[
                newActivityToActivityType[newActivity as NewActivity]
              ]
            }
          </IconContainer>
        ),
        customText: t(
          getEnumTKey("activities", "newActivities", NewActivity, newActivity)
        )
      };
    });
    return options;
  };

  return (
    <>
      <div
        className="flex flex-col gap-2"
        data-test={`${entityType}-upcoming-activities`}
      >
        <div className="flex w-full justify-between items-center">
          <div className="flex gap-2 items-center">
            <Text variant="bodySecondary" className="text-h2">
              {t("deals.nextSteps.upcomingActivities.title")}
            </Text>
            {upcomingActivities?.length > 0 && (
              <CounterBadge count={upcomingActivities.length} size="sm" />
            )}
          </div>
          <OptionsDropdownMenu
            options={getActivityTypeOptions()}
            tPrefix="detailedView.create"
            triggerIcon="add"
            buttonVariant="iconNoBg"
            iconSize="md"
            iconColor="secondary"
            tooltipText={t("deals.nextSteps.upcomingActivities.add")}
          />
        </div>
        {upcomingActivities?.length === 0 ? (
          <div className="flex px-2 py-2 w-full flex-col gap-2 items-center">
            {!isQuickView && <UpcomingActivityIcon />}
            <Text variant="bodyTertiary">
              {t("deals.nextSteps.upcomingActivities.empty")}
            </Text>
          </div>
        ) : (
          map(upcomingActivities, (activity) => (
            <ActivityItem
              key={activity.id}
              activity={activity}
              onEdit={handleOpenEdit}
              onDelete={(activityId) => setDeleteActivityId(activityId)}
            />
          ))
        )}
      </div>
      {activityModals}
      <ConfirmationModal
        isOpen={deleteActivityId !== null && !isDropdownClosing}
        setIsOpen={(open) => {
          if (!open) setDeleteActivityId(null);
        }}
        title={t("activities.activityModal.deleteActivity")}
        confirmationText={t("activities.activityModal.deleteActivity")}
        handleSubmit={handleDeleteActivity}
      />
    </>
  );
};

export default UpcomingActivities;
